import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";

import { SidePanel } from "../shared";
import { SYSTEM_CATEGORY_ITEMS } from "../constants";
import {
  NAV_VISIBILITY_SETTING_KEY,
  isNavItemVisible,
  listToggleableNavGroups,
  normalizeNavVisibility,
} from "../nav-visibility";
import { getWorkspaceSettings, updateWorkspaceSettings } from "../../../api";
import { useLicense } from "../../../LicenseContext";
import { useNotify } from "../../../ToastContext";
import { usePermission } from "../../../UserContext";

// Charts and gauges render through the OpenBridge web components (obc-*)
// loaded by public/index.html — the same vendored bundle the editor and
// published runtime use (with the CS patches: pixel sizing, time-axis
// tooltips, zoom range callbacks). No direct Chart.js usage here.

const _gwConfig = (typeof window !== "undefined" && window.CS_GATEWAY_CONFIG) || {};
const CORE_URL = _gwConfig.CORE_URL || process.env.REACT_APP_CORE_URL || "http://localhost:8090";
const HISTORY_MAX = 288; // 24h at 5-min intervals

// Matches editor's pickInterval logic from historianData.js
const INTERVALS = [
  { label: "1s", ms: 1000 },
  { label: "5s", ms: 5000 },
  { label: "10s", ms: 10000 },
  { label: "30s", ms: 30000 },
  { label: "1m", ms: 60000 },
  { label: "5m", ms: 300000 },
  { label: "15m", ms: 900000 },
  { label: "1h", ms: 3600000 },
];
const TARGET_POINTS = 1000;

function pickInterval(rangeMs) {
  for (const { label, ms } of INTERVALS) {
    if (rangeMs / ms <= TARGET_POINTS) return label;
  }
  return "1h";
}

async function resolveTagPathToId(tagPath) {
  if (!tagPath) return null;
  const res = await fetch(
    `${CORE_URL}/api/tags/by-full-path?path=${encodeURIComponent(tagPath)}`,
    { credentials: "include" }
  );
  if (!res.ok) return null;
  const tag = await res.json();
  return tag?.id ?? tag?.tag_id ?? tag?.ID ?? null;
}

async function fetchHistorianAggregate({ tagId, start, end, interval }) {
  const params = new URLSearchParams({ tag_id: tagId, start, end, interval });
  const res = await fetch(
    `${CORE_URL}/api/history/aggregate?${params}`,
    { credentials: "include" }
  );
  if (!res.ok) return [];
  return res.json();
}

async function fetchTagHistory(tagPath) {
  try {
    const tagId = await resolveTagPathToId(tagPath);
    if (tagId == null) return [];

    const rangeMs = 24 * 60 * 60 * 1000;
    const end = new Date();
    const start = new Date(end.getTime() - rangeMs);
    const interval = pickInterval(rangeMs);

    const buckets = await fetchHistorianAggregate({
      tagId,
      start: start.toISOString(),
      end: end.toISOString(),
      interval,
    });
    if (!Array.isArray(buckets)) return [];
    // Keep the full bucket shape: the trend draws the average as the line
    // and shades the min–max envelope, so aggregated data shows its real
    // excursion range. (Previously this plotted bucket MAX as the only
    // line, which read systematically high vs. the live tail.)
    return buckets.map((b) => {
      const avg = b.avg ?? b.last ?? b.max ?? 0;
      return {
        x: new Date(b.bucket_start ?? b.bucketStart).getTime(),
        y: avg,
        min: b.min ?? avg,
        max: b.max ?? avg,
      };
    });
  } catch {
    return [];
  }
}

// Map display names → keys used internally.
// Actual full_path from server is slash-separated (e.g.
// "default/System/Performance/CPU_Usage"); we match by the last segment
// (lowercased, underscores → spaces).
const SYSTEM_TAG_KEYS = [
  "CPU Usage",
  "Memory Usage",
  "Max Memory",
  "Memory Utilization",
  "Disk Utilization",
  "Available Disk Space",
  "System Name",
  "Uptime Seconds",
  "Current Date Time",
  "Timezone",
];

const SYSTEM_TAG_PATHS = [
  "default/System/Performance/CPU_Usage",
  "default/System/Performance/Memory_Usage",
  "default/System/Performance/Max_Memory",
  "default/System/Performance/Memory_Utilization",
  "default/System/Performance/Disk_Utilization",
  "default/System/Performance/Available_Disk_Space",
  "default/System/System_Name",
  "default/System/Uptime_Seconds",
  "default/System/Current_Date_Time",
  "default/System/Timezone",
];

function tagKeyFromPath(fullPath) {
  // e.g. "default/System/Performance/CPU_Usage" → last segment → "CPU_Usage" → "cpu usage".
  // Accepts both `/` and `.` separators so lingering legacy paths still
  // resolve if any sneak through before all producers are migrated.
  const last = fullPath.split(/[/.]/).pop() || "";
  return last.replace(/_/g, " ").toLowerCase();
}

const SYSTEM_KEY_SET = new Set(SYSTEM_TAG_KEYS.map((k) => k.toLowerCase()));

function formatBytes(bytes) {
  if (bytes == null || !Number.isFinite(bytes)) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
}

function formatUptime(seconds) {
  if (seconds == null || !Number.isFinite(seconds)) return "—";
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function formatPct(val) {
  if (val == null || !Number.isFinite(val)) return "—";
  return `${val.toFixed(1)}%`;
}

function useSystemTags() {
  const [tags, setTags] = useState({});
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const reconnectAttempts = useRef(0);

  useEffect(() => {
    let disposed = false;

    function connect() {
      if (disposed) return;
      const base = CORE_URL.replace(/^http/, "ws");
      const params = new URLSearchParams({
        paths: SYSTEM_TAG_PATHS.join(","),
      });
      const ws = new WebSocket(`${base}/ws?${params.toString()}`);
      wsRef.current = ws;

      ws.onopen = () => { reconnectAttempts.current = 0; };

      ws.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data);
          if (msg.type === "pong" || msg.type === "ping") return;
          const entries = msg.tags || msg.Tags || (Array.isArray(msg) ? msg : []);
          if (!entries.length) return;
          setTags((prev) => {
            const next = { ...prev };
            entries.forEach((t) => {
              const fullPath = t.full_path || t.fullPath || t.path || t.name || "";
              if (!fullPath) return;
              const key = tagKeyFromPath(fullPath);
              if (!SYSTEM_KEY_SET.has(key)) return;
              const raw = t.value ?? t.Value;
              const num = Number(raw);
              next[key] = Number.isFinite(num) ? num : raw;
            });
            return next;
          });
        } catch { /* ignore non-JSON */ }
      };

      ws.onclose = () => {
        if (disposed) return;
        const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
        reconnectTimer.current = setTimeout(() => {
          reconnectAttempts.current += 1;
          connect();
        }, delay);
      };

      ws.onerror = () => ws.close();
    }

    connect();

    // Keepalive ping
    const pingInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: "ping" }));
      }
    }, 25000);

    return () => {
      disposed = true;
      clearInterval(pingInterval);
      clearTimeout(reconnectTimer.current);
      if (wsRef.current) wsRef.current.close();
    };
  }, []);

  return tags;
}

const WS_DEFAULTS = {
  homepageRedirect: "/editor",
  runtimeAccess: "private",
  sidebarSections: {},
};

// Compares what the sidebar would actually render, not the raw maps — so
// unticking a box and ticking it back settles the Save button again, even
// though the second click leaves behind an explicit value where there was
// none before.
const sameSidebarSections = (a, b) => {
  const ids = new Set([...Object.keys(a || {}), ...Object.keys(b || {})]);
  for (const id of ids) {
    if (isNavItemVisible(a, id) !== isNavItemVisible(b, id)) return false;
  }
  return true;
};

const SystemSettings = () => {
  const canConfigure = usePermission("configure");
  const isAdmin = usePermission("admin");
  const { licensingEnabled, products, loading: licenseLoading } = useLicense();
  const notify = useNotify();
  const [settings, setSettings] = useState(WS_DEFAULTS);
  const [savedSettings, setSavedSettings] = useState(WS_DEFAULTS);
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    try {
      const res = await getWorkspaceSettings();
      const ws = res?.settings || {};
      const merged = {
        homepageRedirect: ws.homepageRedirect || WS_DEFAULTS.homepageRedirect,
        runtimeAccess: ws.runtimeAccess || WS_DEFAULTS.runtimeAccess,
        sidebarSections: normalizeNavVisibility(ws[NAV_VISIBILITY_SETTING_KEY]),
      };
      setSettings(merged);
      setSavedSettings(merged);
    } catch (err) {
      console.error("Failed to load workspace settings:", err);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!cancelled) await reload();
    })();
    return () => { cancelled = true; };
  }, [reload]);

  // Refresh when the AI updates Gateway Settings.
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.resource === "workspace_settings") reload();
    };
    window.addEventListener("gw-ai-resource-changed", handler);
    return () => window.removeEventListener("gw-ai-resource-changed", handler);
  }, [reload]);

  const hasChanges =
    settings.homepageRedirect !== savedSettings.homepageRedirect ||
    settings.runtimeAccess !== savedSettings.runtimeAccess ||
    !sameSidebarSections(settings.sidebarSections, savedSettings.sidebarSections);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateWorkspaceSettings({
        homepageRedirect: settings.homepageRedirect,
        runtimeAccess: settings.runtimeAccess,
        [NAV_VISIBILITY_SETTING_KEY]: settings.sidebarSections,
      });
      setSavedSettings({ ...settings });
      // Repaints the sidebar in place — GatewayApp owns the nav and re-reads
      // workspace settings on this event.
      window.dispatchEvent(new CustomEvent("gw-nav-visibility-changed"));
      notify({ type: "success", title: "Saved", message: "Workspace settings updated." });
    } catch (err) {
      notify({ type: "error", title: "Error", message: err.message || "Failed to save settings." });
      console.error("Save workspace settings failed:", err);
    } finally {
      setSaving(false);
    }
  };

  // Items gated away from this user (admin-only, unlicensed, other SKU) get
  // no checkbox — their stored values ride along untouched because the toggle
  // merges into the existing map.
  const sidebarGroups = useMemo(
    () => listToggleableNavGroups({
      isAdmin,
      licensingEnabled,
      products: licenseLoading ? [] : products,
    }),
    [isAdmin, licensingEnabled, licenseLoading, products]
  );

  const toggleSection = (id, next) =>
    setSettings((s) => ({
      ...s,
      sidebarSections: { ...s.sidebarSections, [id]: next },
    }));

  const isPublic = settings.runtimeAccess === "public";
  const toggleRuntime = () =>
    setSettings((s) => ({
      ...s,
      runtimeAccess: s.runtimeAccess === "public" ? "private" : "public",
    }));

  return (
    <div className="gwContentGrid">
      <section className="gwFormStack">
        <div className="gwPageHeader">
          <div>
            <div className="gwPageTitle">Gateway Settings</div>
            <div className="gwPageSubtitle">
              Configure routing and access for this workspace.
            </div>
          </div>
          <button
            className="gwPrimaryBtn"
            type="button"
            disabled={!hasChanges || saving || !canConfigure}
            title={canConfigure ? undefined : "Requires configure permission"}
            onClick={handleSave}
          >
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>

        {/* ── Routing ── */}
        <div className="gwCardSection" data-gw-card="workspace_routing">
          <div className="gwSectionTitle">Routing</div>
          <div className="gwCardSectionBody">
            <div className="gwField">
              <label className="gwLabel">Homepage Redirect</label>
              <input
                className="gwInput"
                value={settings.homepageRedirect}
                onChange={(e) =>
                  setSettings((s) => ({ ...s, homepageRedirect: e.target.value }))
                }
                placeholder="/editor"
                disabled={!canConfigure}
                title={canConfigure ? undefined : "Requires configure permission"}
              />
              <div className="gwHint">
                Path that the root URL (/) redirects to. Use <strong>/editor</strong> for the editor,
                or a page path like <strong>/my-project/home</strong>. Pages remain accessible at their original URLs.
              </div>
            </div>
          </div>
        </div>

        {/* ── Sidebar ── */}
        <div className="gwCardSection" data-gw-card="workspace_sidebar">
          <div className="gwSectionTitle">Sidebar</div>
          <div className="gwCardSectionBody">
            <div className="gwHint">
              Pick the sections that appear in the gateway's left navigation.
              Unchecking one only declutters the nav — the page stays reachable
              from the topbar search and from its direct link.
            </div>
            {sidebarGroups.map((group) => (
              <div className="gwSidebarSectionGroup" key={group.title}>
                <div className="gwSidebarSectionTitle">{group.title}</div>
                <div className="gwCheckboxGrid">
                  {group.items.map((item) => (
                    <label
                      className="gwCheckboxRow"
                      key={item.id}
                      title={canConfigure ? undefined : "Requires configure permission"}
                    >
                      <input
                        type="checkbox"
                        checked={isNavItemVisible(settings.sidebarSections, item.id)}
                        disabled={!canConfigure}
                        onChange={(e) => toggleSection(item.id, e.target.checked)}
                      />
                      <span>{item.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Access Control ── */}
        <div className="gwCardSection" data-gw-card="workspace_access">
          <div className="gwSectionTitle">Access Control</div>
          <div className="gwCardSectionBody">
            <div className="gwToggleRow">
              <div>
                <div className="gwLabel">Public Runtime Pages</div>
                <div className="gwHint">
                  Allow anyone on the internet to view published runtime pages
                  without logging in. When off, visitors are redirected to the
                  login screen.
                </div>
              </div>
              <div
                className={`gwToggle ${isPublic ? "isOn" : ""} ${canConfigure ? "" : "isDisabled"}`}
                role="switch"
                aria-checked={isPublic}
                aria-disabled={!canConfigure}
                tabIndex={canConfigure ? 0 : -1}
                title={canConfigure ? undefined : "Requires configure permission"}
                onClick={canConfigure ? toggleRuntime : undefined}
                onKeyDown={canConfigure ? (e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    toggleRuntime();
                  }
                } : undefined}
              >
                <span />
              </div>
            </div>

            {isPublic && (
              <div
                className="gwNotice"
                style={{
                  borderColor: "rgba(255, 193, 7, 0.4)",
                  background: "rgba(255, 193, 7, 0.08)",
                }}
              >
                Published pages are publicly visible. The editor and gateway
                still require authentication.
              </div>
            )}
          </div>
        </div>
      </section>

      <SidePanel title="Categories" items={SYSTEM_CATEGORY_ITEMS} />
    </div>
  );
};

/* ── Theme helpers ─────────────────────────────────────────────────── */

function isLightTheme() {
  return document.documentElement.getAttribute("data-theme") === "light";
}

// Series identity colors (axis/grid/tick styling now comes from the
// OpenBridge theme, so only the per-metric line/fill colors remain).
function chartColors() {
  const light = isLightTheme();
  return {
    cpu: light ? "rgb(60, 120, 240)" : "rgb(80, 140, 255)",
    cpuFill: light ? "rgba(60, 120, 240, 0.14)" : "rgba(80, 140, 255, 0.12)",
    mem: "rgb(76, 175, 80)",
    memFill: light ? "rgba(76, 175, 80, 0.14)" : "rgba(76, 175, 80, 0.12)",
  };
}

/* ── OpenBridge chart wrappers ──────────────────────────────────────── */

// Wall-clock x-tick labels. OB's own timeDisplay only offers "minutes since
// start" or full dates — useless on a 24h utilization window. Same approach
// as the editor's applyClockTicks: patch the RAW chart config (config._config,
// never the live options proxy) after each dataset assignment, because OB
// rebuilds the chart config wholesale on every data update.
function formatClockTick(value) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const h = d.getHours();
  const mm = String(d.getMinutes()).padStart(2, "0");
  const ampm = h >= 12 ? "PM" : "AM";
  return `${h % 12 || 12}:${mm} ${ampm}`;
}

// Post-build repairs on the underlying chart. OB rebuilds the chart config
// wholesale from element properties on every data update, and that rebuild
// (a) drops custom tick callbacks, (b) rewrites the band's relative fill
// target ("-1" → "start"), and (c) stomps its backgroundColor to
// "transparent" — so the fixes re-apply after each dataset assignment.
// Full update() on change, not update("none"): Chart.js caches resolved
// element options per animation mode and a "none" update would repaint the
// band with the stale transparent background (same trap the editor's
// applyDatasetTweaks documents).
function applyChartRepairs(el, fillColor) {
  const chart = el?.chart;
  if (!chart) return;
  const xScale = chart.config?._config?.options?.scales?.x;
  if (xScale) {
    xScale.ticks = { ...(xScale.ticks || {}), callback: formatClockTick, maxRotation: 0 };
    // End the axis at the newest sample. The default bounds:"ticks" pads
    // hours past "now" to reach a round tick, which reads as a flatline.
    xScale.bounds = "data";
  }
  const ds = chart.data?.datasets || [];
  const maxIdx = ds.findIndex((d) => d.label === "max");
  const minIdx = ds.findIndex((d) => d.label === "min");
  if (maxIdx >= 0 && minIdx >= 0) {
    const min = ds[minIdx];
    if (min.fill !== maxIdx) min.fill = maxIdx;
    if (min.backgroundColor !== fillColor) min.backgroundColor = fillColor;
  }
  try {
    chart.update();
  } catch {
    /* mid-render — next data tick re-applies */
  }
}

// Wait for an obc-* custom element definition (the bundle loads deferred).
// Rendering the element only after it's defined sidesteps every
// property-before-upgrade pitfall.
function useObReady(tag) {
  const [ready, setReady] = useState(
    () => typeof window !== "undefined" && !!window.customElements?.get(tag)
  );
  useEffect(() => {
    if (ready || !window.customElements?.whenDefined) return undefined;
    let mounted = true;
    window.customElements.whenDefined(tag).then(() => {
      if (mounted) setReady(true);
    });
    return () => { mounted = false; };
  }, [tag, ready]);
  return ready;
}

// Pixel-size an OB chart element to its wrapper — the elements default to a
// fixed 480×320 canvas otherwise. (width/height are actual pixels while
// fixedAspectRatioScaling stays off, and the CS-patched __csObApplySize
// resizes the live chart without a flash.)
function useObSize(ready, wrapRef, elRef) {
  useEffect(() => {
    if (!ready) return undefined;
    const wrap = wrapRef.current;
    const el = elRef.current;
    if (!wrap || !el) return undefined;
    const apply = () => {
      const r = wrap.getBoundingClientRect();
      if (r.width > 4 && r.height > 4) {
        el.width = Math.round(r.width);
        el.height = Math.round(r.height);
      }
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [ready, wrapRef, elRef]);
}

function TrendChart({ history, lineColor, fillColor, label }) {
  const wrapRef = useRef(null);
  const elRef = useRef(null);
  const ready = useObReady("obc-line-graph");
  const [zoomed, setZoomed] = useState(false);
  const zoomedRef = useRef(false);

  // One-time chart configuration.
  useEffect(() => {
    if (!ready) return;
    const el = elRef.current;
    if (!el) return;
    el.xAxisType = "time";
    // Straight segments: plot samples as measured — bezier smoothing invents
    // values between points, which is exactly the "doesn't represent the
    // state" complaint on a utilization trend.
    el.lineMode = "straight";
    el.showPoints = false;
    el.showGrid = true;
    el.showGridX = false;
    el.showGridY = true;
    el.showTickMarks = true;
    el.legend = false;
    el.unit = "%";
    // Utilization is 0–100 by definition; autoscaling would magnify noise.
    // id "y" overrides Chart.js's default scale — any other id ADDS a second
    // axis next to the default one.
    el.yAxes = [{ id: "y", position: "left", min: 0, max: 100 }];
    // CS-patched zoom/pan hook (drag pans, wheel/pinch zooms).
    el.__csObOnRangeChange = () => {
      if (!zoomedRef.current) {
        zoomedRef.current = true;
        setZoomed(true);
      }
    };
  }, [ready]);

  useObSize(ready, wrapRef, elRef);

  // Data: solid average line over a translucent min–max envelope, so each
  // aggregated bucket shows its real excursion range (spikes stay visible
  // instead of being averaged away — or worse, only-max being shown).
  useEffect(() => {
    if (!ready) return;
    const el = elRef.current;
    if (!el) return;
    const hasBand = history.some(
      (p) => Number.isFinite(p.min) && Number.isFinite(p.max) && p.max !== p.min
    );
    const datasets = [];
    if (hasBand) {
      datasets.push({
        label: "max",
        data: history.map((p) => ({ x: p.x, y: p.max ?? p.y })),
        borderColor: "transparent",
        backgroundColor: fillColor,
        borderWidth: 0,
        pointRadius: 0,
        fill: false,
      });
      datasets.push({
        label: "min",
        data: history.map((p) => ({ x: p.x, y: p.min ?? p.y })),
        borderColor: "transparent",
        backgroundColor: fillColor,
        borderWidth: 0,
        pointRadius: 0,
        fill: "-1", // shade between min and max
      });
    }
    datasets.push({
      label: label || "avg",
      data: history.map((p) => ({ x: p.x, y: p.y })),
      borderColor: lineColor,
      backgroundColor: fillColor,
      borderWidth: 1.5,
      pointRadius: 0,
      fill: false,
    });

    // Preserve an active zoom/pan window across live-data reassignment
    // (mirrors the editor's assignDatasetsPreservingZoom).
    const chart = el.chart;
    let savedRange = null;
    if (chart?.isZoomedOrPanned?.()) {
      const x = chart.scales?.x;
      if (x && Number.isFinite(x.min) && Number.isFinite(x.max)) {
        savedRange = { min: x.min, max: x.max };
      }
    }
    el.datasets = datasets;
    window.requestAnimationFrame(() => {
      applyChartRepairs(el, fillColor);
      if (savedRange) {
        try {
          el.chart?.zoomScale?.("x", savedRange, "none");
        } catch {
          /* chart mid-recreate — next data tick re-applies */
        }
      }
    });
  }, [ready, history, lineColor, fillColor, label]);

  const resetZoom = () => {
    try { elRef.current?.chart?.resetZoom?.(); } catch { /* not created yet */ }
    zoomedRef.current = false;
    setZoomed(false);
  };

  return (
    <div className="gwTrendChartInner" ref={wrapRef}>
      {ready && <obc-line-graph ref={elRef} />}
      {zoomed && (
        <button
          type="button"
          onClick={resetZoom}
          className="gwGhostBtn gwTrendResetBtn"
        >
          Reset
        </button>
      )}
    </div>
  );
}

function GaugeChart({ value, label }) {
  const elRef = useRef(null);
  const ready = useObReady("obc-gauge-radial");

  useEffect(() => {
    if (!ready) return;
    const el = elRef.current;
    if (!el) return;
    el.minValue = 0;
    el.maxValue = 100;
    el.type = "bar";
    el.showLabels = true;
    el.primaryTickmarkInterval = 25;
    el.secondaryTickmarkInterval = 5;
    // Enhanced tickmarks draw from the primary/secondary color vars. That
    // frees --instrument-tick-mark-tertiary-color — which the default
    // "regular" style shares between tickmarks AND the un-triggered advice
    // band hatch — to be overridden in cards.css so caution zones read
    // orange at all times, not only while the value sits inside them.
    el.tickmarkStyle = "enhanced";
    // Real threshold zones on the scale, matching the stat-card hints
    // (Elevated ≥70, High ≥90). Both use "caution" (orange): OB's only other
    // zone type, "advice", renders TEAL — which reads as "good" on a
    // utilization gauge — and there is no red "alarm" type in 1.0.1 (it
    // renders nothing). Two segments so the 90+ arc lights up separately
    // when utilization actually gets there.
    el.advices = [
      { minValue: 70, maxValue: 90, type: "caution", hinted: false },
      { minValue: 90, maxValue: 100, type: "caution", hinted: false },
    ];
  }, [ready]);

  useEffect(() => {
    if (!ready) return;
    const el = elRef.current;
    if (!el) return;
    const v = value != null && Number.isFinite(value) ? value : 0;
    el.value = Math.max(0, Math.min(100, v));
  }, [ready, value]);

  return (
    <div className="gwGaugeInner">
      {ready && <obc-gauge-radial ref={elRef} />}
      <div className="gwGaugeLabel">
        <span className="gwGaugePct">
          {value != null && Number.isFinite(value) ? `${value.toFixed(1)}%` : "—"}
        </span>
        <span className="gwGaugeName">{label}</span>
      </div>
    </div>
  );
}

const PlatformOverview = () => {
  const tags = useSystemTags();
  const [cpuHistory, setCpuHistory] = useState([]);
  const [memHistory, setMemHistory] = useState([]);

  const cpuUsage = tags["cpu usage"];
  const memUsage = tags["memory usage"];
  const maxMem = tags["max memory"];
  const memUtil = tags["memory utilization"];
  const diskUtil = tags["disk utilization"];
  const diskFree = tags["available disk space"];
  const systemName = tags["system name"];
  const uptimeSec = tags["uptime seconds"];
  const dateTime = tags["current date time"];
  const timezone = tags["timezone"];

  const connected = Object.keys(tags).length > 0;

  // Load 24h historian data on mount
  const historyLoaded = useRef(false);
  useEffect(() => {
    if (historyLoaded.current) return;
    historyLoaded.current = true;
    Promise.all([
      fetchTagHistory("default/System/Performance/CPU_Usage"),
      fetchTagHistory("default/System/Performance/Memory_Utilization"),
    ]).then(([cpu, mem]) => {
      if (cpu.length) setCpuHistory(cpu);
      if (mem.length) setMemHistory(mem);
    });
  }, []);

  // Append live data from WebSocket
  const prevCpu = useRef(undefined);
  const prevMem = useRef(undefined);
  useEffect(() => {
    if (cpuUsage !== prevCpu.current && cpuUsage != null) {
      prevCpu.current = cpuUsage;
      // Live samples are instantaneous: min = max = value, so the historian
      // min–max envelope collapses to the line for the live tail.
      setCpuHistory((h) => [...h.slice(-(HISTORY_MAX - 1)), { x: Date.now(), y: cpuUsage, min: cpuUsage, max: cpuUsage }]);
    }
  }, [cpuUsage]);
  useEffect(() => {
    if (memUtil !== prevMem.current && memUtil != null) {
      prevMem.current = memUtil;
      setMemHistory((h) => [...h.slice(-(HISTORY_MAX - 1)), { x: Date.now(), y: memUtil, min: memUtil, max: memUtil }]);
    }
  }, [memUtil]);

  return (
    <div className="gwContentGrid">
      <section className="gwFormStack">
        <div className="gwPageHeader">
          <div>
            <div className="gwPageTitle">Platform Overview</div>
            <div className="gwPageSubtitle">
              {systemName || "Live health, sessions, and operational summaries."}
              {connected && (
                <span style={{ marginLeft: 8, fontSize: 10, color: "var(--gw-success, #4caf50)" }}>● Live</span>
              )}
            </div>
          </div>
        </div>

        {/* Stat cards */}
        <div className="gwStatsGrid" data-gw-card="overview_stats">
          <div className="gwStatCard" data-gw-stat="cpu">
            <div className="gwStatLabel">CPU Usage</div>
            <div className="gwStatValue">{formatPct(cpuUsage)}</div>
            <div className="gwStatHint">{cpuUsage == null ? "Waiting for data…" : cpuUsage < 70 ? "Normal load" : cpuUsage < 90 ? "Elevated load" : "High load"}</div>
          </div>
          <div className="gwStatCard" data-gw-stat="memory">
            <div className="gwStatLabel">Memory</div>
            <div className="gwStatValue">{formatBytes(memUsage)}</div>
            <div className="gwStatHint">{maxMem != null ? `${formatBytes(maxMem)} total · ${formatPct(memUtil)}` : "Waiting for data…"}</div>
          </div>
          <div className="gwStatCard" data-gw-stat="disk">
            <div className="gwStatLabel">Disk</div>
            <div className="gwStatValue">{formatPct(diskUtil)}</div>
            <div className="gwStatHint">{diskFree != null ? `${formatBytes(diskFree)} free` : "Waiting for data…"}</div>
          </div>
          <div className="gwStatCard" data-gw-stat="uptime">
            <div className="gwStatLabel">Uptime</div>
            <div className="gwStatValue">{formatUptime(uptimeSec)}</div>
            <div className="gwStatHint">{uptimeSec != null ? "No restarts detected" : "Waiting for data…"}</div>
          </div>
        </div>

        {/* Separate CPU and Memory trend charts */}
        <div className="gwTrendGrid">
          <div className="gwCardSection" data-gw-card="cpu_trend">
            <div className="gwSectionTitle">CPU Trend</div>
            <div className="gwCardSectionBody gwOverviewBody">
              <div className="gwTrendChartWrap">
                <TrendChart history={cpuHistory} label="CPU" lineColor={chartColors().cpu} fillColor={chartColors().cpuFill} />
              </div>
            </div>
          </div>
          <div className="gwCardSection" data-gw-card="memory_trend">
            <div className="gwSectionTitle">Memory Trend</div>
            <div className="gwCardSectionBody gwOverviewBody">
              <div className="gwTrendChartWrap">
                <TrendChart history={memHistory} label="Memory" lineColor={chartColors().mem} fillColor={chartColors().memFill} />
              </div>
            </div>
          </div>
        </div>

        {/* Utilization gauges */}
        <div className="gwCardSection" data-gw-card="resource_gauges">
          <div className="gwSectionTitle">Resource Utilization</div>
          <div className="gwCardSectionBody gwOverviewBody">
            <div className="gwGaugeGrid">
              <div className="gwGaugeWrap">
                <GaugeChart value={cpuUsage} label="CPU" />
              </div>
              <div className="gwGaugeWrap">
                <GaugeChart value={memUtil} label="Memory" />
              </div>
              <div className="gwGaugeWrap">
                <GaugeChart value={diskUtil} label="Disk" />
              </div>
            </div>
          </div>
        </div>

        {/* System info */}
        {(dateTime || timezone) && (
          <div className="gwCardSection" data-gw-card="system_info">
            <div className="gwSectionTitle">System Info</div>
            <div className="gwCardSectionBody gwInfoGrid">
              {dateTime && (
                <div className="gwField">
                  <label className="gwLabel">Date / Time</label>
                  <div className="gwHint">{dateTime}</div>
                </div>
              )}
              {timezone && (
                <div className="gwField">
                  <label className="gwLabel">Timezone</label>
                  <div className="gwHint">{timezone}</div>
                </div>
              )}
              {systemName && (
                <div className="gwField">
                  <label className="gwLabel">System Name</label>
                  <div className="gwHint">{systemName}</div>
                </div>
              )}
            </div>
          </div>
        )}
      </section>
      <SidePanel
        title="Highlights"
        items={[
          "Gateway Status",
          "Performance",
          "Memory",
          "Disk",
          "System Info",
        ]}
      />
    </div>
  );
};

export { SystemSettings, PlatformOverview };
